import { installBreadcrumbs } from './collectors/breadcrumbs';
import { installConsoleCollector } from './collectors/console';
import { installErrorCollector } from './collectors/errors';
import { installNetworkCollector } from './collectors/network';
import { ApiClient, ApiError } from './core/api';
import { fingerprint, Throttle } from './core/fingerprint';
import { SDK_VERSION, State } from './core/state';
import { Transport } from './core/transport';
import { errorToParts, now } from './core/util';
import { drainQueue, readScriptOptions } from './loader';
import type { Command, FETraceApi, Identity, InitOptions, Level, RemoteConfig, WidgetEvent } from './types';
import type { Widget } from './ui/widget';

export type { Announcement, Breadcrumb, Command, FETraceApi, Identity, InitOptions, Level, RecentRequest, Snapshot, Ticket, TicketMessage, WidgetEvent } from './types';

const state = new State();
let api: ApiClient | null = null;
let transport: Transport | null = null;
let widget: Widget | null = null;
let uninstall: (() => void)[] = [];
const throttle = new Throttle(5, 60_000);
const originalFetch = typeof window !== 'undefined' ? window.fetch?.bind(window) : undefined;

function emit(event: WidgetEvent): void {
  if (!transport) return;
  const key = fingerprint(event.method, event.url, event.status_code, event.message);
  if (!throttle.allow(key)) return state.log('throttled', key);
  transport.enqueue({ ...event, breadcrumbs: event.breadcrumbs ?? state.breadcrumbs.last(20), context: { ...state.context, ...(event.context ?? {}) } });
}

/**
 * Dashboard settings (placement, visibility, redaction) arrive with /config, and the UI waits for
 * them: mounting earlier showed the launcher at the default bottom-left. The last good answer is kept
 * per key and used when the request fails; a fresh answer always wins, and a later retry applies live.
 */
let configReady = false;
const CONFIG_RETRY_MS = [5_000, 15_000, 60_000, 300_000];

function applyConfig(remote: RemoteConfig): void {
  state.applyRemote(remote);
  if (remote.limits?.batch_size) transport!.batchSize = remote.limits.batch_size;
  if (remote.limits?.batch_interval_ms) transport!.intervalMs = remote.limits.batch_interval_ms;
}

/** 401/403 means the key or this domain is rejected: every call would fail, so the UI stays off. */
const denied = (err: unknown) => err instanceof ApiError && (err.status === 401 || err.status === 403);

async function loadConfig(): Promise<'ok' | 'fallback' | 'off'> {
  const client = api!;
  try {
    const remote = await client.config();
    if (api !== client) return 'off'; // torn down meanwhile
    applyConfig(remote);
    state.rememberRemote(remote);
    return 'ok';
  } catch (err) {
    if (denied(err)) {
      console.warn('[FETrace] /config refused (%s): check the key and the project\'s allowed domains; the widget stays hidden', (err as ApiError).status);
      return 'off';
    }
    state.log('config failed, using the last known settings', err);
    const cached = state.cachedRemote();
    if (cached) applyConfig(cached);
    retryConfig(client, 0);
    return 'fallback';
  }
}

function retryConfig(client: ApiClient, attempt: number): void {
  if (attempt >= CONFIG_RETRY_MS.length) return;
  setTimeout(() => {
    if (api !== client) return;
    client
      .config()
      .then((remote) => {
        if (api !== client) return;
        applyConfig(remote);
        state.rememberRemote(remote);
        widget?.applyRemote();
        void syncUi();
      })
      .catch((err) => (denied(err) ? undefined : retryConfig(client, attempt + 1)));
  }, CONFIG_RETRY_MS[attempt]);
}

function init(options: InitOptions): void {
  if (state.initialized) return;
  if (!options?.key) {
    console.warn('[FETrace] init() requires a public key');
    return;
  }
  state.configure(options);
  consumeTicketLink();
  api = new ApiClient(state, originalFetch);
  transport = new Transport(state, api);

  // Collectors first so nothing is missed while config/UI load.
  uninstall.push(installErrorCollector(state, emit));
  uninstall.push(installBreadcrumbs(state, () => void syncUi()));
  if (state.options.captureNetwork) uninstall.push(installNetworkCollector(state, emit));
  if (state.options.captureConsole) uninstall.push(installConsoleCollector(state, emit));
  transport.start();

  const boot = async () => {
    if ((await loadConfig()) === 'off') return;
    configReady = true;
    await mountUi();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => void boot(), { once: true });
  else void boot();
}

/**
 * Mounts the launcher/panel unless this page is one the UI stays off (login pages by
 * default — see core/visibility). Collectors keep running either way.
 */
async function mountUi(): Promise<void> {
  if (widget || !api) return;
  if (state.uiHidden()) return state.log('ui hidden on', location.pathname);
  const { Widget } = await import('./ui/widget');
  // The route (or the whole SDK) may have changed while the chunk loaded.
  if (widget || !api || state.uiHidden()) return;
  widget = new Widget(state, api);
  widget.startPolling(state.remote.limits?.poll_interval_ms ?? 60_000);
  // A screen recording started on an earlier page of this tab carries on here (DECISIONS.md §110).
  void widget.resumeReplay();
  // A "Nasıl yapılır" guide started on an earlier page walks on here (DECISIONS.md §213).
  void widget.resumeGuide();
  if (pendingOpen) {
    pendingOpen = false;
    widget.open();
  }
  if (pendingTicket !== null) {
    widget.showTicket(pendingTicket);
    pendingTicket = null;
  }
}

let pendingOpen = false;
let pendingTicket: number | null = null;
let mounting: Promise<void> | null = null;

/** SPA route change: drop the UI when entering a hidden page, bring it back when leaving one. */
async function syncUi(): Promise<void> {
  // Before /config has answered, boot() does the first mount (with the dashboard settings).
  if (!api || !configReady) return;
  if (state.uiHidden()) {
    if (!widget) return;
    widget.destroy();
    widget = null;
    pendingOpen = false;

    return;
  }
  if (widget || mounting) return;
  mounting = mountUi();
  try {
    await mounting;
  } finally {
    mounting = null;
  }
}

/**
 * Staff-initiated conversations reach unverified users by e-mail; the link ends with
 * `#fetrace-ticket=<id>.<token>`. Remember the token (same store as tickets created here) and open the thread.
 */
function consumeTicketLink(): void {
  if (typeof location === 'undefined') return;
  const m = /(?:^#|[#&])fetrace-ticket=(\d+)\.([A-Za-z0-9]+)/.exec(location.hash || '');
  if (!m) return;
  const id = Number(m[1]);
  state.rememberTicket(id, m[2]!);
  pendingTicket = id;
  try {
    const rest = location.hash.replace(/(?:^#|[#&])fetrace-ticket=\d+\.[A-Za-z0-9]+/, '').replace(/^[#&]+/, '');
    history.replaceState(history.state, '', location.pathname + location.search + (rest ? '#' + rest : ''));
  } catch {
    /* history unavailable (sandboxed) — the fragment is harmless */
  }
}

function identify(identity: Identity): void {
  if (!identity?.id) return;
  state.identity = { ...identity, id: String(identity.id) };
  widget?.startPolling(state.remote.limits?.poll_interval_ms ?? 60_000);
}

function setContext(key: string, value: unknown): void {
  state.context[key] = value;
}

function captureMessage(message: string, level: Level = 'info'): void {
  emit({ type: 'custom', level, message: state.redactor.text(String(message)).slice(0, 2000), url: location.href, occurred_at: now() });
}

function captureException(error: unknown): void {
  const parts = errorToParts(error);
  const message = state.redactor.text(parts.message).slice(0, 2000);
  const stack = parts.stack ? state.redactor.text(parts.stack).slice(0, 20000) : undefined;
  state.jsErrors.push({ message, stack, at: now() });
  emit({ type: 'js_error', level: 'error', message, stack, url: location.href, occurred_at: now() });
}

function open(): void {
  if (widget) widget.open();
  else if (state.uiHidden()) state.log('open() ignored, ui hidden on', location.pathname);
  else pendingOpen = true;
}
function close(): void {
  widget?.close();
}
function toggle(): void {
  if (widget) widget.toggle();
  else open();
}

/** Call on logout: forgets identity, context and this browser's ticket tokens. */
function reset(): void {
  state.reset();
  widget?.setUnread(0, 0);
}

function push(command: Command): void {
  const [name, ...args] = command;
  switch (name) {
    case 'init':
      return init(args[0] as InitOptions);
    case 'identify':
      return identify(args[0] as Identity);
    case 'setContext':
      return setContext(args[0] as string, args[1]);
    case 'captureMessage':
      return captureMessage(args[0] as string, args[1] as Level | undefined);
    case 'captureException':
      return captureException(args[0]);
    case 'open':
      return open();
    case 'close':
      return close();
    case 'toggle':
      return toggle();
    case 'reset':
      return reset();
  }
}

export const version: string = SDK_VERSION;

export const FETrace: FETraceApi = { init, identify, setContext, captureMessage, captureException, open, close, toggle, reset, push, version };

export { init, identify, setContext, captureMessage, captureException, open, close, toggle, reset, push };

/** @internal exposed for tests */
export function __getState(): State {
  return state;
}

/** @internal exposed for tests: tears down collectors and UI */
export function __teardown(): void {
  uninstall.forEach((fn) => fn());
  uninstall = [];
  widget?.destroy();
  widget = null;
  transport = null;
  api = null;
  configReady = false;
  state.initialized = false;
  state.remote = {};
  state.reset();
}

// Browser bootstrap: replace the pre-load queue with the real API and auto-init from <script data-key>.
if (typeof window !== 'undefined') {
  const w = window as unknown as { FETrace?: unknown; __fetraceScriptUrl?: string };
  const current = typeof document !== 'undefined' ? (document.currentScript as HTMLScriptElement | null) : null;
  if (current?.src) w.__fetraceScriptUrl = current.src;
  const pending = w.FETrace;
  w.FETrace = FETrace;
  drainQueue(pending, FETrace);

  const scriptOptions = typeof document !== 'undefined' ? readScriptOptions() : null;
  if (scriptOptions?.key && !state.initialized) init(scriptOptions as InitOptions);
}
